// src/Payroll_Management/PayRoll_Report/BataAbstractReport.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer, FileSpreadsheet } from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function fmt2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return s(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// yyyy-mm-dd for input type="date"
function firstDayOfMonthISO(d = new Date()) {
  const dt = new Date(d.getFullYear(), d.getMonth(), 1);
  return dt.toISOString().slice(0, 10);
}
function lastDayOfMonthISO(d = new Date()) {
  const dt = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return dt.toISOString().slice(0, 10);
}

// For Node we are sending yyyy-mm-dd (your SP accepts nvarchar)
function toServerDate(v) {
  return s(v);
}

const BataAbstractReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const canViewAll = s(branchType).toUpperCase() === "HO";

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // meta
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // filters
  const [fromDate, setFromDate] = useState(firstDayOfMonthISO());
  const [toDate, setToDate] = useState(lastDayOfMonthISO());
  const [deptName, setDeptName] = useState("Select");
  const [deptCode, setDeptCode] = useState("Select");
  const [branchId, setBranchId] = useState(canViewAll ? "Select" : s(loginBranchId));

  // list
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-32", []);

  const loadMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing branch info. Please re-login.");
      return;
    }

    try {
      setMetaLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bata-abstract-report/meta`, {
        params: {
          finYearId: toInt(finYearId),
          branchId: toInt(loginBranchId),
          branchType: s(branchType),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);
      setDepartments(res.data.departments || []);

      // defaults
      if (!canViewAll) setBranchId(s(loginBranchId));
      else setBranchId((res.data.defaultBranchId && String(res.data.defaultBranchId)) || "Select");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta error");
    } finally {
      setMetaLoading(false);
    }
  };

  const validateDates = () => {
    const f = s(fromDate);
    const t = s(toDate);

    // ASP logic: if one missing => alert
    if ((!!f && !t) || (!f && !!t)) {
      toast.warn("Select Date");
      return false;
    }

    // allow both empty? ASP auto-defaults; but we already defaulted.
    if (!f && !t) return true;

    const fd = new Date(f);
    const td = new Date(t);
    if (Number.isNaN(fd.getTime()) || Number.isNaN(td.getTime())) {
      toast.warn("Invalid date");
      return false;
    }
    if (fd > td) {
      toast.warn("FromDate should be <= ToDate");
      return false;
    }
    return true;
  };

  const fetchList = async (p = 1) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!validateDates()) return;

    const effectiveBranchId = canViewAll ? s(branchId) : s(loginBranchId);

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bata-abstract-report/data`, {
        params: {
          finYearId: toInt(finYearId),
          loginBranchId: s(loginBranchId),
          branchType: s(branchType),
          branchId: effectiveBranchId,

          depName: deptName !== "Select" ? deptName : "",
          depCode: deptCode !== "Select" ? deptCode : "",

          fromDate: toServerDate(fromDate),
          toDate: toServerDate(toDate),

          page: p,
          pageSize,
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Load failed");
        return;
      }

      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(res.data.page || p);

      // Grand total for current page only is in ASP footer for whole dataset.
      // We compute grand total from ALL rows only if backend returns; currently not.
      // We'll compute grand total from visible rows like summary; if you want true GT, add it in node.
      const gt = (res.data.rows || []).reduce((sum, r) => {
        const v = Number(r?.fld_totalamount);
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0);
      setGrandTotal(gt);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Load error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // load default grid like ASP
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // page change fetch
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const onViewAll = async () => {
    setDeptName("Select");
    setDeptCode("Select");
    setFromDate(firstDayOfMonthISO());
    setToDate(lastDayOfMonthISO());
    if (canViewAll) setBranchId(s(loginBranchId) || "Select");
    setPage(1);
    await fetchList(1);
  };

  // Department mutually exclusive (ASP)
  const onDeptNameChange = (val) => {
    setDeptName(val);
    if (val !== "Select") setDeptCode("Select");
  };
  const onDeptCodeChange = (val) => {
    setDeptCode(val);
    if (val !== "Select") setDeptName("Select");
  };

  // Print page navigation (same tab) - you'll create Print page next like Conveyance
  const goPrint = () => {
    if (!validateDates()) return;

    const effectiveBranchId = canViewAll ? s(branchId) : s(loginBranchId);

    const qs = new URLSearchParams({
      finYearId: s(finYearId),
      loginBranchId: s(loginBranchId),
      branchType: s(branchType),
      branchId: effectiveBranchId,
      depName: deptName !== "Select" ? deptName : "",
      depCode: deptCode !== "Select" ? deptCode : "",
      fromDate: s(fromDate),
      toDate: s(toDate),
    }).toString();

    navigate(`/PayRoll/RataAbstractReportPrintMain?${qs}`);
  };

  const exportExcel = async () => {
  if (!s(finYearId)) {
    toast.warn("Please select Financial Year.");
    return;
  }
  if (!validateDates()) return;

  const effectiveBranchId = canViewAll ? s(branchId) : s(loginBranchId);

  try {
    setLoading(true);

    const res = await axios.get(`${BASE_URL}/api/bata-abstract-report/excel-data`, {
      params: {
        finYearId: toInt(finYearId),
        loginBranchId: s(loginBranchId),
        branchType: s(branchType),
        branchId: effectiveBranchId,
        fromDate: toServerDate(fromDate),
        toDate: toServerDate(toDate),
      },
    });

    if (!res?.data?.success) {
      toast.error(res?.data?.message || "Excel export failed");
      return;
    }

    const rows = res.data.rows || [];

    // Build HTML Excel like ASP (Excel reads HTML table)
    const esc = (x) =>
      s(x)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const branchLabel =
      canViewAll
        ? (branches.find((b) => String(b.fld_branchid) === String(effectiveBranchId))?.fld_branchname || "Select")
        : (branches.find((b) => String(b.fld_branchid) === String(loginBranchId))?.fld_branchname || "Login Branch");

    // ASP headings:
    const hname = [
      "S_NO",
      "EMPLOYEE CODE",
      "EMPLOYEE NAME",
      "DESIGNATION",
      "BANK ACC_NO",
      "BATA",
      "CLA",
      "TOT-AMT",
      "MEALS",
      "RATE",
      "TOTAL",
      "T.V",
      "OTHERS",
      "TOTAL DED",
      "NET AMT",
    ];

    // NOTE: Your SP returns columns in some order.
    // We'll write by header keys if present, else fallback to index.
    const getVal = (r, key, idxFallback) => {
      if (r && Object.prototype.hasOwnProperty.call(r, key)) return r[key];
      const keys = Object.keys(r || {});
      return r?.[keys[idxFallback]] ?? "";
    };

    const bodyRows = rows
      .map((r, i) => {
        return `
          <tr>
            <td>${i + 1}</td>
            <td>${esc(getVal(r, "fld_employeecode", 0))}</td>
            <td>${esc(getVal(r, "fld_employee", 1))}</td>
            <td>${esc(getVal(r, "fld_designationname", 2))}</td>
            <td>${esc(getVal(r, "fld_accountno", 3))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_bata", 4))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_cla", 5))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_totamt", 6))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_meals", 7))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_rate", 8))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_total", 9))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_tv", 10))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_others", 11))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_totalded", 12))}</td>
            <td style="text-align:right;">${esc(getVal(r, "fld_netamt", 13))}</td>
          </tr>
        `;
      })
      .join("");

    const html = `
      <!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.0 Transitional//EN">
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Bata Abstract Report</title>
        </head>
        <body>
          <font style="font-size:10.0pt; font-family:Calibri;">
            <br/><br/><br/>
            <table border="1" bgcolor="#ffffff" bordercolor="#000000" cellspacing="0" cellpadding="4"
              style="font-size:10.0pt; font-family:Calibri; background:white; border-collapse:collapse; width:100%;">
              <tr>
                <td colspan="15" align="center"><h3>SRC PROJECTS PRIVATE LIMITED</h3></td>
              </tr>
              <tr>
                <td colspan="15" align="center"><h3>Branch : ${esc(branchLabel)}</h3></td>
              </tr>
              <tr>
                <td colspan="15" align="center"><h3>Bata report for the period of ${esc(fromDate)} to ${esc(toDate)}</h3></td>
              </tr>
              <tr>
                ${hname.map((h) => `<td><b>${esc(h)}</b></td>`).join("")}
              </tr>
              ${bodyRows || `<tr><td colspan="15" align="center">No Records Found</td></tr>`}
            </table>
          </font>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `Bata_Abstract_Report_${fromDate}_to_${toDate}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success("Excel downloaded");
  } catch (e) {
    toast.error(e?.response?.data?.message || e.message || "Excel export error");
  } finally {
    setLoading(false);
  }
};


  // Department link click: in ASP it opens DutyCardReportPrintNew.aspx?Type=3 with depid/depcod/dep in session.
  // In React we will navigate to a detail print route and pass dept info in query.
  const openDeptDetail = (r) => {
    if (!validateDates()) return;

    const effectiveBranchId = canViewAll ? s(branchId) : s(loginBranchId);

    const qs = new URLSearchParams({
      finYearId: s(finYearId),
      loginBranchId: s(loginBranchId),
      branchType: s(branchType),
      branchId: effectiveBranchId,

      depid: s(r?.fld_deptid),
      depcode: s(r?.fld_deptcode),
      depname: s(r?.fld_departmentname),

      fromDate: s(fromDate),
      toDate: s(toDate),
      type: "3", // mirrors ASP Type=3
    }).toString();

   navigate(
  `/PayRoll/BataDepartmentReportPrint?finYearId=${finYearId}&loginBranchId=${loginBranchId}&branchType=${branchType}` +
  `&branchId=${branchId}&depId=${r.fld_deptid}&depCode=${r.fld_deptcode}&depName=${encodeURIComponent(r.fld_departmentname)}` +
  `&fromDate=${fromDate}&toDate=${toDate}`
);

  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATA ABSTRACT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Bata Abstract Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / View All / Pagination / Print
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={goPrint} className={primaryBtn} disabled={loading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              {/* Excel is next step; you can wire download later */}
              <button onClick={exportExcel} className={ghostBtn} disabled={loading || metaLoading}>
  <FileSpreadsheet className="w-4 h-4" />
  Excel
</button>

            </div>
          </div>

          {/* Filters */}
          <form onSubmit={onSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              <div>
                <label className={labelBase}>From Date</label>
                <input
                  type="date"
                  className={inputBase}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div>
                <label className={labelBase}>To Date</label>
                <input
                  type="date"
                  className={inputBase}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div>
                <label className={labelBase}>Branch</label>
                <select
                  className={inputBase}
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={!canViewAll}
                  title={!canViewAll ? "Branch fixed to login branch" : ""}
                >
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Department Name</label>
                <select className={inputBase} value={deptName} onChange={(e) => onDeptNameChange(e.target.value)}>
                  <option value="Select">Select</option>
                  {departments.map((d) => (
                    <option key={d.fld_deptid} value={d.fld_departmentname}>
                      {d.fld_departmentname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Department Code</label>
                <select className={inputBase} value={deptCode} onChange={(e) => onDeptCodeChange(e.target.value)}>
                  <option value="Select">Select</option>
                  {departments.map((d) => (
                    <option key={d.fld_deptid} value={d.fld_deptcode}>
                      {d.fld_deptcode}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end gap-3">
                <button type="submit" className={primaryBtn} disabled={loading || metaLoading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>

                <button type="button" onClick={onViewAll} className={ghostBtn} disabled={loading || metaLoading}>
                  <RefreshCcw className="w-4 h-4" />
                  View All
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Department Code</th>
                    <th className="px-4 py-4 text-left font-bold">Department Name</th>
                    <th className="px-4 py-4 text-right font-bold">Total Amount</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 4 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map((r, idx) => (
                        <tr key={`${r.fld_deptid}-${idx}`} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_deptcode}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            <button
                              type="button"
                              onClick={() => openDeptDetail(r)}
                              className="underline font-bold hover:opacity-80"
                              title="Open department detail (Type=3)"
                            >
                              {r.fld_departmentname}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {fmt2(r.fld_totalamount)}
                          </td>
                        </tr>
                      ))}

                      {/* Footer row (page total) */}
                      <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className={`px-4 py-4 text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`} colSpan={3}>
                          Grand Total :
                        </td>
                        <td className={`px-4 py-4 text-sm font-bold text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {fmt2(grandTotal)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default BataAbstractReport;
